import { EpisodeOfCareModel } from '@ctw/shared/api/fhir/models/episode-of-care';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { MAX_OBJECTS_PER_REQUEST, fqsRequestAll } from '@ctw/shared/api/fqs/client';
import {
  type EpisodeOfCareGraphqlResponse,
  episodeOfCareQuery,
} from '@ctw/shared/api/fqs/queries/episode-of-care';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { QUERY_KEY_PATIENT_EPISODES_OF_CARE } from '@ctw/shared/utils/query-keys';
import type { GraphQLClient } from 'graphql-request';

interface GetPatientEpisodesOfCareArgs {
  telemetry: ReturnType<typeof useTelemetry>;
  graphqlClient: GraphQLClient;
  patient: PatientModel;
}

export async function getPatientEpisodesOfCare({
  telemetry,
  graphqlClient,
  patient,
}: GetPatientEpisodesOfCareArgs) {
  const { data } = await fqsRequestAll<EpisodeOfCareGraphqlResponse>(
    telemetry,
    graphqlClient,
    episodeOfCareQuery,
    'EpisodeOfCareConnection',
    {
      upid: patient.UPID,
      cursor: '',
      first: MAX_OBJECTS_PER_REQUEST,
      filter: {
        tag: {
          allmatch: ['https://zusapi.com/summary|TransitionsOfCare'],
        },
      },
    },
  );

  const nodes = data.EpisodeOfCareConnection.edges.map((x) => x.node);

  return nodes.map((c) => new EpisodeOfCareModel(c, []));
}

export const usePatientEpisodesOfCare = () =>
  usePatientQuery({
    queryId: QUERY_KEY_PATIENT_EPISODES_OF_CARE,
    queryFn: async ({ graphqlClient, telemetry, patient }) => {
      return await getPatientEpisodesOfCare({
        telemetry,
        graphqlClient,
        patient,
      });
    },
  });
